import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Settings, Plus, Trash2, Save, RefreshCw, Check, X,
  ChevronUp, ChevronDown, Palette, ArrowRight, MapPin,
  Link2, Zap, GripVertical, AlertTriangle, Edit2, LayoutDashboard, Power, Sparkles,
  Monitor, Filter, List, LayoutGrid, Layers, ChevronRight, Eye
} from 'lucide-react';
import getApiUrl from '../../utils/getApiUrl';
import PipelineGroupedPills from '../PipelineGroupedPills';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Palette de 40+ couleurs variées
const COLOR_PALETTE = [
  // Bleus
  { color: '#3B82F6', name: 'Bleu', keywords: ['nouveau', 'lead', 'prospect', 'contact', 'entrant'] },
  { color: '#0EA5E9', name: 'Bleu ciel', keywords: ['appel', 'téléphone', 'call'] },
  { color: '#06B6D4', name: 'Cyan', keywords: ['info', 'information', 'attente'] },
  { color: '#0891B2', name: 'Bleu canard', keywords: ['analyse', 'étude', 'audit'] },
  { color: '#1E40AF', name: 'Bleu roi', keywords: ['important', 'priorité', 'urgent'] },
  { color: '#1D4ED8', name: 'Bleu cobalt', keywords: ['pro', 'professionnel'] },
  { color: '#2563EB', name: 'Bleu vif', keywords: ['actif', 'en cours'] },
  
  // Violets / Pourpres
  { color: '#8B5CF6', name: 'Violet', keywords: ['r1', 'rdv', 'rendez-vous', 'planifié'] },
  { color: '#A855F7', name: 'Pourpre', keywords: ['premium', 'vip', 'spécial'] },
  { color: '#7C3AED', name: 'Violet vif', keywords: ['technique', 'tech'] },
  { color: '#6D28D9', name: 'Violet foncé', keywords: ['expert', 'senior'] },
  { color: '#9333EA', name: 'Violet électrique', keywords: ['flash', 'rapide'] },
  { color: '#C026D3', name: 'Magenta', keywords: ['créatif', 'design'] },
  { color: '#D946EF', name: 'Fuchsia', keywords: ['marketing', 'com'] },
  
  // Roses
  { color: '#EC4899', name: 'Rose', keywords: ['femme', 'senior', 'adapté'] },
  { color: '#F472B6', name: 'Rose clair', keywords: ['doux', 'calme'] },
  { color: '#DB2777', name: 'Rose vif', keywords: ['alerte', 'attention'] },
  { color: '#BE185D', name: 'Rose foncé', keywords: ['critique'] },
  
  // Rouges / Oranges
  { color: '#EF4444', name: 'Rouge', keywords: ['perdu', 'annulé', 'refus', 'non', 'échec', 'bloqué'] },
  { color: '#DC2626', name: 'Rouge vif', keywords: ['stop', 'arrêt', 'fin'] },
  { color: '#B91C1C', name: 'Rouge foncé', keywords: ['erreur', 'problème'] },
  { color: '#F97316', name: 'Orange', keywords: ['rappel', 'rappeler', 'relance', 'suivi'] },
  { color: '#EA580C', name: 'Orange foncé', keywords: ['warning', 'attention'] },
  { color: '#FB923C', name: 'Orange clair', keywords: ['modéré', 'moyen'] },
  { color: '#F59E0B', name: 'Ambre', keywords: ['confirmation', 'confirmer', 'valider'] },
  { color: '#FBBF24', name: 'Jaune doré', keywords: ['or', 'premium', 'gold'] },
  
  // Jaunes
  { color: '#EAB308', name: 'Jaune', keywords: ['en attente', 'pause', 'stand-by'] },
  { color: '#FDE047', name: 'Jaune citron', keywords: ['nouveau', 'frais'] },
  { color: '#FACC15', name: 'Jaune vif', keywords: ['soleil', 'positif'] },
  
  // Verts
  { color: '#10B981', name: 'Émeraude', keywords: ['validé', 'ok', 'succès', 'gagné', 'signé', 'terminé'] },
  { color: '#22C55E', name: 'Vert', keywords: ['fait', 'fini', 'complet', 'accompli'] },
  { color: '#16A34A', name: 'Vert foncé', keywords: ['confirmé', 'approuvé'] },
  { color: '#14B8A6', name: 'Teal', keywords: ['chantier', 'travaux', 'intervention'] },
  { color: '#0D9488', name: 'Teal foncé', keywords: ['terrain', 'site'] },
  { color: '#059669', name: 'Vert émeraude', keywords: ['réussi', 'positif'] },
  { color: '#84CC16', name: 'Lime', keywords: ['go', 'départ', 'lancer'] },
  { color: '#65A30D', name: 'Lime foncé', keywords: ['nature', 'éco'] },
  
  // Gris / Neutres
  { color: '#6B7280', name: 'Gris', keywords: ['inactif', 'archivé', 'ancien'] },
  { color: '#374151', name: 'Gris foncé', keywords: ['final', 'clos', 'fermé'] },
  { color: '#4B5563', name: 'Gris moyen', keywords: ['neutre', 'standard'] },
  { color: '#9CA3AF', name: 'Gris clair', keywords: ['secondaire', 'optionnel'] },
  { color: '#78716C', name: 'Gris chaud', keywords: ['historique', 'passé'] },
  { color: '#57534E', name: 'Gris taupe', keywords: ['admin', 'administratif'] },
  
  // Marrons / Terres
  { color: '#92400E', name: 'Marron', keywords: ['terrain', 'construction'] },
  { color: '#B45309', name: 'Marron clair', keywords: ['bois', 'naturel'] },
];

// Les dashboards sont maintenant chargés dynamiquement depuis l'API

// Fonction pour suggérer une couleur basée sur la description
const suggestColor = (description, nom, existingColors = []) => {
  const text = `${nom} ${description}`.toLowerCase();
  
  // Chercher une couleur qui correspond aux mots-clés
  let bestMatch = null;
  let bestScore = 0;
  
  for (const colorItem of COLOR_PALETTE) {
    // Skip si la couleur est déjà utilisée
    if (existingColors.includes(colorItem.color)) continue;
    
    let score = 0;
    for (const keyword of colorItem.keywords) {
      if (text.includes(keyword.toLowerCase())) {
        score += keyword.length; // Plus le mot-clé est long, plus il est pertinent
      }
    }
    
    if (score > bestScore) {
      bestScore = score;
      bestMatch = colorItem.color;
    }
  }
  
  // Si aucun match, prendre une couleur non utilisée au hasard
  if (!bestMatch) {
    const availableColors = COLOR_PALETTE.filter(c => !existingColors.includes(c.color));
    if (availableColors.length > 0) {
      bestMatch = availableColors[Math.floor(Math.random() * availableColors.length)].color;
    } else {
      // Toutes les couleurs sont utilisées, prendre la première
      bestMatch = COLOR_PALETTE[0].color;
    }
  }
  
  return bestMatch;
};

// Liste de tous les départements français
const TOUS_DEPARTEMENTS = [
  '01', '02', '03', '04', '05', '06', '07', '08', '09', '10',
  '11', '12', '13', '14', '15', '16', '17', '18', '19', '21',
  '22', '23', '24', '25', '26', '27', '28', '29', '2A', '2B',
  '30', '31', '32', '33', '34', '35', '36', '37', '38', '39',
  '40', '41', '42', '43', '44', '45', '46', '47', '48', '49',
  '50', '51', '52', '53', '54', '55', '56', '57', '58', '59',
  '60', '61', '62', '63', '64', '65', '66', '67', '68', '69',
  '70', '71', '72', '73', '74', '75', '76', '77', '78', '79',
  '80', '81', '82', '83', '84', '85', '86', '87', '88', '89',
  '90', '91', '92', '93', '94', '95', '971', '972', '973', '974', '976'
];

export default function PipelineConfigPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('etapes');
  
  // Data
  const [etapes, setEtapes] = useState([]);
  const [zones, setZones] = useState([]);
  const [sources, setSources] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [groupes, setGroupes] = useState([]);
  
  // Edit state
  const [editingEtape, setEditingEtape] = useState(null);
  const [newDpt, setNewDpt] = useState('');
  const [editingSource, setEditingSource] = useState(null);
  
  // Filter & view state for étapes
  const [etapesDashFilter, setEtapesDashFilter] = useState(null); // null = all, 'none' = unassigned, or dashboard id
  const [etapesViewMode, setEtapesViewMode] = useState('cards'); // 'cards' or 'table'
  
  // Groupes management state
  const [newGroupLabel, setNewGroupLabel] = useState('');
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingGroupLabel, setEditingGroupLabel] = useState('');
  const [savingGroupes, setSavingGroupes] = useState(false);
  const [previewDashboard, setPreviewDashboard] = useState('all');
  
  // Charger la config
  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      // Charger config pipeline + liste globale des dashboards + catégories + groupes en parallèle
      const [respPipeline, respDash, respCats, respGroupes] = await Promise.all([
        authFetch(`${API}/config/pipeline/all`),
        authFetch(`${API}/config/dashboards`),
        authFetch(`${API}/config/pipeline/categories`),
        authFetch(`${API}/config/pipeline/groupes`)
      ]);
      if (respPipeline.ok) {
        const data = await respPipeline.json();
        setEtapes(data.etapes || []);
        setZones(data.zones || []);
        setSources(data.sources || []);
        
        // Les dashboards sélectionnés pour le pipeline (sauvegardés en DB)
        const savedPipelineDashboards = data.dashboards || [];
        const savedIds = new Set(savedPipelineDashboards.map(d => d.id));
        
        // Charger la liste globale des dashboards de l'app
        if (respDash.ok) {
          const dashData = await respDash.json();
          const allAppDashboards = dashData.dashboards || {};
          
          // Construire la liste: chaque dashboard de l'app + son état actif/inactif pour le pipeline
          const fullList = Object.entries(allAppDashboards)
            .sort(([,a], [,b]) => (a.order || 99) - (b.order || 99))
            .map(([key, config]) => ({
              id: key,
              nom: config.label || key,
              description: config.description || '',
              actif: savedIds.size > 0 ? savedIds.has(key) : false
            }));
          
          // Ajouter "Aucun" en fin de liste
          fullList.push({ id: 'aucun', nom: 'Aucun (non affiché)', description: 'Étape non visible dans aucun dashboard', actif: true });
          
          setDashboards(fullList);
        }
        
        // Charger les catégories
        if (respCats.ok) {
          const catsData = await respCats.json();
          setCategories(catsData.categories || []);
        }
        
        // Charger les groupes pipeline
        if (respGroupes.ok) {
          const groupesData = await respGroupes.json();
          setGroupes(groupesData.groupes || []);
        }
      }
    } catch (e) {
      console.error('Erreur chargement config:', e);
    }
    setLoading(false);
  }, []);
  
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);
  
  // Helper: get dashboards array for an étape (handles legacy single 'dashboard' field)
  const getEtapeDashboards = (etape) => {
    if (etape.dashboards && etape.dashboards.length > 0) return etape.dashboards;
    if (etape.dashboard) return [etape.dashboard];
    return [];
  };
  
  // ============== ÉTAPES ==============

  // Filtered étapes based on dashboard filter
  const filteredEtapes = useMemo(() => {
    if (!etapesDashFilter) return etapes;
    if (etapesDashFilter === 'none') {
      return etapes.filter(e => {
        const d = getEtapeDashboards(e).filter(x => x !== 'aucun');
        return d.length === 0;
      });
    }
    return etapes.filter(e => getEtapeDashboards(e).includes(etapesDashFilter));
  }, [etapes, etapesDashFilter, dashboards]);

  const unassignedCount = useMemo(() => {
    return etapes.filter(e => {
      const d = getEtapeDashboards(e).filter(x => x !== 'aucun');
      return d.length === 0;
    }).length;
  }, [etapes, dashboards]);

  // Categories depuis la config DB (avec fallback sur celles trouvées dans les étapes)
  const availableCategories = useMemo(() => {
    if (categories.length > 0) return categories;
    const CATEGORY_LABELS = { avant_r1: 'Avant R1', apres_r1: 'Après R1', final: 'Final' };
    const cats = [...new Set(etapes.map(e => e.categorie).filter(Boolean))];
    return cats.map(c => ({ id: c, label: CATEGORY_LABELS[c] || c }));
  }, [categories, etapes]);

  const moveEtape = (index, direction) => {
    const newEtapes = [...etapes];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newEtapes.length) return;
    
    [newEtapes[index], newEtapes[targetIndex]] = [newEtapes[targetIndex], newEtapes[index]];
    newEtapes.forEach((e, i) => e.ordre = i + 1);
    setEtapes(newEtapes);
  };
  
  const updateEtape = (index, field, value) => {
    const newEtapes = [...etapes];
    newEtapes[index] = { ...newEtapes[index], [field]: value };
    setEtapes(newEtapes);
  };
  
  const deleteEtape = (index) => {
    if (!confirm(`Supprimer l'étape "${etapes[index].nom}" ?`)) return;
    setEtapes(etapes.filter((_, i) => i !== index));
  };
  
  const addEtape = () => {
    const newId = `etape_${Date.now()}`;
    setEtapes([...etapes, {
      id: newId,
      nom: 'Nouvelle étape',
      description: '',
      color: '#3B82F6',
      ordre: etapes.length + 1,
      next: [],
      categorie: 'avant_r1',
      dashboard: 'telepro',
      dashboards: ['telepro'],
      actif: true
    }]);
    setEditingEtape(etapes.length);
  };
  
  const saveEtapes = async () => {
    setSaving(true);
    try {
      const resp = await authFetch(`${API}/config/pipeline/etapes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(etapes)
      });
      if (resp.ok) {
        alert('✅ Étapes sauvegardées');
      } else {
        const err = await resp.json().catch(() => ({}));
        alert('❌ Erreur sauvegarde: ' + (err.detail || resp.statusText));
      }
    } catch (e) {
      alert('❌ Erreur: ' + e.message);
    }
    setSaving(false);
  };
  
  // ============== ZONES ==============
  
  const toggleZone = (dpt) => {
    if (zones.includes(dpt)) {
      setZones(zones.filter(d => d !== dpt));
    } else {
      setZones([...zones, dpt].sort());
    }
  };
  
  const saveZones = async () => {
    setSaving(true);
    try {
      const resp = await authFetch(`${API}/config/pipeline/zones`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ departements: zones })
      });
      if (resp.ok) {
        alert('✅ Zones sauvegardées');
      }
    } catch (e) {
      alert('❌ Erreur: ' + e.message);
    }
    setSaving(false);
  };
  
  // ============== SOURCES ==============
  
  const updateSource = (index, field, value) => {
    const newSources = [...sources];
    newSources[index] = { ...newSources[index], [field]: value };
    setSources(newSources);
  };
  
  const deleteSource = (index) => {
    if (!confirm(`Supprimer la source "${sources[index].nom}" ?`)) return;
    setSources(sources.filter((_, i) => i !== index));
  };
  
  const addSource = () => {
    const newId = `source_${Date.now()}`;
    setSources([...sources, {
      id: newId,
      nom: 'Nouvelle source',
      type: 'formulaire',
      webhook_url: '/api/webhook/nouvelle-source',
      actif: true
    }]);
    setEditingSource(sources.length);
  };
  
  const saveSources = async () => {
    setSaving(true);
    try {
      const resp = await authFetch(`${API}/config/pipeline/sources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sources)
      });
      if (resp.ok) {
        alert('Ources sauvegardées');
      }
    } catch (e) {
      alert('Erreur: ' + e.message);
    }
    setSaving(false);
  };
  
  // ============== DASHBOARDS ==============
  
  const toggleDashboardActive = (index) => {
    const newDashboards = [...dashboards];
    newDashboards[index] = { ...newDashboards[index], actif: !newDashboards[index].actif };
    setDashboards(newDashboards);
  };
  
  const saveDashboards = async () => {
    setSaving(true);
    try {
      // Ne sauvegarder que les dashboards actifs (sélectionnés pour le pipeline)
      const activeDashboards = dashboards.filter(d => d.actif).map(d => ({
        id: d.id,
        nom: d.nom,
        icon: '',
        actif: true
      }));
      const resp = await authFetch(`${API}/config/pipeline/dashboards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(activeDashboards)
      });
      if (resp.ok) {
        alert('Dashboards pipeline sauvegardés');
      }
    } catch (e) {
      alert('Erreur: ' + e.message);
    }
    setSaving(false);
  };
  
  // ============== RESET ==============
  
  const resetConfig = async () => {
    if (!confirm('⚠️ Réinitialiser toute la configuration par défaut ?')) return;
    setSaving(true);
    try {
      const resp = await authFetch(`${API}/config/pipeline/reset`, { method: 'POST' });
      if (resp.ok) {
        alert('✅ Configuration réinitialisée');
        loadConfig();
      }
    } catch (e) {
      alert('❌ Erreur: ' + e.message);
    }
    setSaving(false);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Settings className="w-6 h-6 text-indigo-400" />
            Configuration Pipeline
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Gérez les étapes, zones d'intervention et sources de leads
          </p>
        </div>
        <button
          onClick={resetConfig}
          disabled={saving}
          className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Réinitialiser
        </button>
      </div>
      
      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700 pb-2">
        {[
          { id: 'etapes', label: 'Étapes Pipeline', count: etapes.length },
          { id: 'groupes', label: 'Groupes Pipeline', count: groupes.length },
          { id: 'zones', label: "Zones d'intervention", count: zones.length },
          { id: 'sources', label: 'Sources de leads', count: sources.length },
          { id: 'dashboards', label: 'Dashboards', count: dashboards.filter(d => d.id !== 'aucun').length },
          { id: 'categories', label: 'Catégories', count: categories.length },
        ].map(tab => (
          <button
            key={tab.id}
            data-testid={`pipeline-tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-t-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === tab.id
                ? 'bg-indigo-500/20 text-indigo-400 border-b-2 border-indigo-400'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            {tab.label}
            <span className="px-2 py-0.5 bg-slate-700 rounded-full text-xs">{tab.count}</span>
          </button>
        ))}
      </div>
      
      {/* TAB: ÉTAPES */}
      {activeTab === 'etapes' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-400">
              Définissez les étapes du parcours client et leurs transitions
            </p>
            <div className="flex gap-2">
              <button
                onClick={addEtape}
                className="px-3 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg text-sm flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Ajouter étape
              </button>
              <button
                onClick={saveEtapes}
                disabled={saving}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Sauvegarder
              </button>
            </div>
          </div>

          {/* Filtres rapides + Toggle vue */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <Filter className="w-4 h-4 text-slate-500" />
              <button
                onClick={() => setEtapesDashFilter(null)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  !etapesDashFilter ? 'bg-slate-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                Toutes ({etapes.length})
              </button>
              {dashboards.filter(d => d.actif !== false && d.id !== 'aucun').map(d => {
                const count = etapes.filter(e => getEtapeDashboards(e).includes(d.id)).length;
                return (
                  <button
                    key={d.id}
                    onClick={() => setEtapesDashFilter(etapesDashFilter === d.id ? null : d.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      etapesDashFilter === d.id ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                    }`}
                  >
                    {d.nom} ({count})
                  </button>
                );
              })}
              <button
                onClick={() => setEtapesDashFilter(etapesDashFilter === 'none' ? null : 'none')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  etapesDashFilter === 'none' ? 'bg-red-600 text-white' : unassignedCount > 0 ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                <span className="flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Non attribués ({unassignedCount})
                </span>
              </button>
            </div>
            <div className="flex items-center gap-1 bg-slate-800 rounded-lg p-1">
              <button
                onClick={() => setEtapesViewMode('cards')}
                className={`p-1.5 rounded ${etapesViewMode === 'cards' ? 'bg-slate-600 text-white' : 'text-slate-400 hover:text-white'}`}
                title="Vue cartes"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setEtapesViewMode('table')}
                className={`p-1.5 rounded ${etapesViewMode === 'table' ? 'bg-slate-600 text-white' : 'text-slate-400 hover:text-white'}`}
                title="Vue tableau"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* VUE TABLEAU */}
          {etapesViewMode === 'table' && (
            <div className="overflow-x-auto rounded-xl border border-slate-700">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-800/80 text-slate-400 text-xs uppercase">
                    <th className="px-3 py-2 text-left">#</th>
                    <th className="px-3 py-2 text-left">Couleur</th>
                    <th className="px-3 py-2 text-left">Nom</th>
                    <th className="px-3 py-2 text-left">ID</th>
                    <th className="px-3 py-2 text-left">Catégorie</th>
                    <th className="px-3 py-2 text-left">Dashboards</th>
                    <th className="px-3 py-2 text-left">Groupe</th>
                    <th className="px-3 py-2 text-center">Actif</th>
                    <th className="px-3 py-2 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEtapes.map((etape, idx) => {
                    const realIndex = etapes.indexOf(etape);
                    const etapeDashboards = getEtapeDashboards(etape).filter(x => x !== 'aucun');
                    return (
                      <tr key={`table-${realIndex}`} className="border-t border-slate-700/50 hover:bg-slate-800/50">
                        <td className="px-3 py-2 text-xs text-slate-500 font-mono">{etape.ordre}</td>
                        <td className="px-3 py-2">
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: etape.color }} />
                        </td>
                        <td className="px-3 py-2 font-medium text-white">{etape.nom}</td>
                        <td className="px-3 py-2 text-xs text-slate-500 font-mono">{etape.id}</td>
                        <td className="px-3 py-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            etape.categorie === 'avant_r1' ? 'bg-blue-500/20 text-blue-400' :
                            etape.categorie === 'apres_r1' ? 'bg-purple-500/20 text-purple-400' :
                            etape.categorie === 'final' ? 'bg-slate-500/20 text-slate-400' :
                            'bg-amber-500/20 text-amber-400'
                          }`}>
                            {availableCategories.find(c => c.id === etape.categorie)?.label || etape.categorie || '-'}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {etapeDashboards.length === 0 ? (
                            <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-400 inline-flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              Aucun
                            </span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {etapeDashboards.map(dId => (
                                <span key={dId} className="text-xs px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-400">
                                  {dashboards.find(d => d.id === dId)?.nom || dId}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {(() => {
                            const grp = groupes.find(g => g.id === etape.groupe_pipeline);
                            if (!grp) return <span className="text-xs text-slate-600">—</span>;
                            return (
                              <span className="text-xs px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                                style={{ backgroundColor: `${grp.color}20`, color: grp.color }}>
                                {grp.label}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {etape.actif !== false ? (
                            <span className="text-xs text-emerald-400">Oui</span>
                          ) : (
                            <span className="text-xs text-red-400">Non</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => { setEtapesViewMode('cards'); setEditingEtape(realIndex); }}
                            className="p-1 hover:bg-slate-700 rounded"
                          >
                            <Edit2 className="w-3.5 h-3.5 text-slate-400" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* VUE CARTES */}
          {etapesViewMode === 'cards' && (
          <div className="space-y-2">
            {filteredEtapes.map((etape) => {
              const index = etapes.indexOf(etape);
              return (
              <div
                key={`etape-${index}`}
                className={`bg-slate-800/50 border rounded-xl p-4 transition-all ${
                  editingEtape === index ? 'border-indigo-500' : 'border-slate-700'
                }`}
              >
                <div className="flex items-center gap-4">
                  {/* Grip & Order */}
                  <div className="flex flex-col items-center gap-1">
                    <button onClick={() => moveEtape(index, 'up')} disabled={index === 0} className="p-1 hover:bg-slate-700 rounded disabled:opacity-30">
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <span className="text-xs text-slate-500 font-mono">{etape.ordre}</span>
                    <button onClick={() => moveEtape(index, 'down')} disabled={index === etapes.length - 1} className="p-1 hover:bg-slate-700 rounded disabled:opacity-30">
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Color indicator */}
                  <div 
                    className="w-4 h-12 rounded-full"
                    style={{ backgroundColor: etape.color }}
                  />
                  
                  {/* Content */}
                  <div className="flex-1">
                    {editingEtape === index ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-4 gap-3">
                          <div>
                            <label className="text-xs text-slate-400">ID (unique)</label>
                            <input
                              key={`id-${index}`}
                              type="text"
                              value={etape.id}
                              onChange={e => updateEtape(index, 'id', e.target.value)}
                              className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm"
                              autoComplete="off"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-slate-400">Nom</label>
                            <input
                              key={`nom-${index}`}
                              type="text"
                              value={etape.nom}
                              onChange={e => updateEtape(index, 'nom', e.target.value)}
                              className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm"
                              autoComplete="off"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-slate-400">Catégorie</label>
                            <select
                              value={etape.categorie}
                              onChange={e => updateEtape(index, 'categorie', e.target.value)}
                              className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm"
                            >
                              {availableCategories.map(c => (
                                <option key={c.id} value={c.id}>{c.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-slate-400">Dashboards associés</label>
                            <div className="mt-1 space-y-1 max-h-40 overflow-y-auto bg-slate-900 border border-slate-600 rounded-lg p-2">
                              {dashboards.filter(d => d.actif !== false).map(d => {
                                const etapeDashboards = getEtapeDashboards(etape);
                                const isSelected = etapeDashboards.includes(d.id);
                                return (
                                  <label key={d.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-800 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => {
                                        let current = [...etapeDashboards];
                                        if (d.id === 'aucun') {
                                          current = isSelected ? [] : ['aucun'];
                                        } else {
                                          current = current.filter(x => x !== 'aucun');
                                          if (isSelected) {
                                            current = current.filter(x => x !== d.id);
                                          } else {
                                            current.push(d.id);
                                          }
                                        }
                                        const newEtapes = [...etapes];
                                        newEtapes[index] = { ...newEtapes[index], dashboards: current, dashboard: current[0] || 'aucun' };
                                        setEtapes(newEtapes);
                                      }}
                                      className="w-4 h-4 rounded border-slate-500"
                                    />
                                    <span className="text-sm text-white">{d.nom}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-slate-400">Description</label>
                            <input
                              type="text"
                              value={etape.description}
                              onChange={e => updateEtape(index, 'description', e.target.value)}
                              className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-slate-400">Groupe Pipeline</label>
                            <select
                              value={etape.groupe_pipeline || ''}
                              onChange={e => updateEtape(index, 'groupe_pipeline', e.target.value)}
                              className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm"
                              style={etape.groupe_pipeline && groupes.find(g => g.id === etape.groupe_pipeline) ? { borderColor: `${groupes.find(g => g.id === etape.groupe_pipeline).color}60` } : {}}
                            >
                              <option value="">— Aucun groupe —</option>
                              {groupes.map(g => (
                                <option key={g.id} value={g.id}>{g.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="flex items-end">
                            <label className="flex items-center gap-3 cursor-pointer">
                              <div 
                                onClick={() => updateEtape(index, 'actif', !etape.actif)}
                                className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${
                                  etape.actif !== false ? 'bg-emerald-500' : 'bg-slate-600'
                                }`}
                              >
                                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                                  etape.actif !== false ? 'translate-x-7' : 'translate-x-1'
                                }`} />
                              </div>
                              <span className={`text-sm font-medium ${etape.actif !== false ? 'text-emerald-400' : 'text-slate-400'}`}>
                                {etape.actif !== false ? 'Étape active' : 'Étape désactivée'}
                              </span>
                            </label>
                          </div>
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-xs text-slate-400">Couleur</label>
                            <button
                              onClick={() => {
                                const existingColors = etapes.filter((_, i) => i !== index).map(e => e.color);
                                const suggested = suggestColor(etape.description || '', etape.nom || '', existingColors);
                                updateEtape(index, 'color', suggested);
                              }}
                              className="flex items-center gap-1 px-2 py-1 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded text-xs transition-colors"
                              title="Suggérer une couleur basée sur la description"
                            >
                              <Sparkles className="w-3 h-3" />
                              Suggestion IA
                            </button>
                          </div>
                          <div className="grid grid-cols-12 gap-1.5">
                            {COLOR_PALETTE.map(c => {
                              const isUsed = etapes.some((e, i) => i !== index && e.color === c.color);
                              return (
                                <button
                                  key={c.color}
                                  onClick={() => updateEtape(index, 'color', c.color)}
                                  className={`w-6 h-6 rounded-md border-2 transition-all ${
                                    etape.color === c.color 
                                      ? 'border-white scale-125 ring-2 ring-white/30' 
                                      : isUsed 
                                        ? 'border-transparent opacity-40 hover:opacity-70' 
                                        : 'border-transparent hover:scale-110'
                                  }`}
                                  style={{ backgroundColor: c.color }}
                                  title={`${c.name}${isUsed ? ' (déjà utilisée)' : ''}`}
                                />
                              );
                            })}
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            Les couleurs estompées sont déjà utilisées par d'autres étapes
                          </p>
                        </div>
                        <div>
                          <label className="text-xs text-slate-400">Transitions possibles (→)</label>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {etapes.filter(e => e.id !== etape.id).map(e => (
                              <button
                                key={e.id}
                                onClick={() => {
                                  const next = etape.next || [];
                                  if (next.includes(e.id)) {
                                    updateEtape(index, 'next', next.filter(n => n !== e.id));
                                  } else {
                                    updateEtape(index, 'next', [...next, e.id]);
                                  }
                                }}
                                className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
                                  (etape.next || []).includes(e.id)
                                    ? 'bg-indigo-500/30 text-indigo-300 border border-indigo-500'
                                    : 'bg-slate-700 text-slate-400 border border-slate-600'
                                }`}
                              >
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: e.color }} />
                                {e.nom}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className={etape.actif === false ? 'opacity-50' : ''}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{etape.nom}</span>
                          <span className="text-xs text-slate-500 font-mono">({etape.id})</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            etape.categorie === 'avant_r1' ? 'bg-blue-500/20 text-blue-400' :
                            etape.categorie === 'apres_r1' ? 'bg-purple-500/20 text-purple-400' :
                            etape.categorie === 'final' ? 'bg-slate-500/20 text-slate-400' :
                            'bg-amber-500/20 text-amber-400'
                          }`}>
                            {availableCategories.find(c => c.id === etape.categorie)?.label || etape.categorie}
                          </span>
                          {/* Badge Dashboard(s) */}
                          {(() => {
                            const etapeDashboards = getEtapeDashboards(etape);
                            const activeDashboards = etapeDashboards.filter(id => id !== 'aucun');
                            if (activeDashboards.length === 0) return null;
                            return activeDashboards.map(dId => (
                              <span key={dId} className="text-xs px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-400 flex items-center gap-1">
                                <LayoutDashboard className="w-3 h-3" />
                                {dashboards.find(d => d.id === dId)?.nom || dId}
                              </span>
                            ));
                          })()}
                          {/* Badge Groupe Pipeline */}
                          {etape.groupe_pipeline && (() => {
                            const grp = groupes.find(g => g.id === etape.groupe_pipeline);
                            if (!grp) return null;
                            return (
                              <span className="text-xs px-2 py-0.5 rounded flex items-center gap-1"
                                style={{ backgroundColor: `${grp.color}20`, color: grp.color }}>
                                <Layers className="w-3 h-3" />
                                {grp.label}
                              </span>
                            );
                          })()}
                          {/* Badge Actif/Inactif */}
                          {etape.actif === false && (
                            <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-400 flex items-center gap-1">
                              <Power className="w-3 h-3" />
                              Désactivée
                            </span>
                          )}
                          {/* Badge Aucun dashboard */}
                          {(() => {
                            const d = getEtapeDashboards(etape).filter(x => x !== 'aucun');
                            if (d.length === 0) return (
                              <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-400 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                Aucun dashboard
                              </span>
                            );
                            return null;
                          })()}
                        </div>
                        <p className="text-sm text-slate-400">{etape.description}</p>
                        {etape.next && etape.next.length > 0 && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
                            <ArrowRight className="w-3 h-3" />
                            {etape.next.map(n => etapes.find(e => e.id === n)?.nom || n).join(', ')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingEtape(editingEtape === index ? null : index)}
                      className="p-2 hover:bg-slate-700 rounded-lg"
                    >
                      {editingEtape === index ? <Check className="w-4 h-4 text-green-400" /> : <Edit2 className="w-4 h-4 text-slate-400" />}
                    </button>
                    <button
                      onClick={() => deleteEtape(index)}
                      className="p-2 hover:bg-red-500/20 rounded-lg text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
          )}
        </div>
      )}
      
      {/* TAB: GROUPES PIPELINE */}
      {activeTab === 'groupes' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* LEFT: Gestion des groupes + Étapes */}
            <div className="space-y-4">
              {/* Groupe management */}
              <div className="bg-slate-800/70 border border-violet-500/30 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-violet-400 flex items-center gap-2">
                    <Layers className="w-4 h-4" />
                    Groupes Pipeline
                  </h3>
                  <button
                    data-testid="save-groupes-btn"
                    onClick={async () => {
                      setSavingGroupes(true);
                      try {
                        const resp = await authFetch(`${API}/config/pipeline/groupes`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify(groupes)
                        });
                        if (resp.ok) {
                          alert('Groupes sauvegardés');
                        } else {
                          const err = await resp.json().catch(() => ({}));
                          alert('Erreur: ' + (err.detail || resp.statusText));
                        }
                      } catch (e) {
                        alert('Erreur: ' + e.message);
                      }
                      setSavingGroupes(false);
                    }}
                    disabled={savingGroupes}
                    className="px-3 py-1.5 bg-violet-600 hover:bg-violet-500 text-white text-xs rounded-lg font-medium flex items-center gap-1.5"
                  >
                    <Save className="w-3.5 h-3.5" />
                    {savingGroupes ? 'Sauvegarde...' : 'Sauvegarder groupes'}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {groupes.map(g => (
                    <div key={g.id} className="relative group/pill inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium"
                      style={{ backgroundColor: `${g.color}20`, color: g.color, border: `1px solid ${g.color}40` }}>
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: g.color }} />
                      {editingGroupId === g.id ? (
                        <input
                          type="text"
                          value={editingGroupLabel}
                          onChange={e => setEditingGroupLabel(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              if (editingGroupLabel.trim()) {
                                setGroupes(prev => prev.map(gr => gr.id === g.id ? { ...gr, label: editingGroupLabel.trim() } : gr));
                              }
                              setEditingGroupId(null);
                            }
                            if (e.key === 'Escape') setEditingGroupId(null);
                          }}
                          onBlur={() => {
                            if (editingGroupLabel.trim()) {
                              setGroupes(prev => prev.map(gr => gr.id === g.id ? { ...gr, label: editingGroupLabel.trim() } : gr));
                            }
                            setEditingGroupId(null);
                          }}
                          autoFocus
                          className="bg-transparent border-b border-current outline-none text-xs w-24 font-medium"
                          style={{ color: g.color }}
                        />
                      ) : (
                        <span>{g.label}</span>
                      )}
                      <span className="text-[10px] bg-slate-800 text-slate-300 rounded-full px-1.5 py-0.5">
                        {etapes.filter(e => e.groupe_pipeline === g.id).length}
                      </span>
                      {editingGroupId !== g.id && (
                        <div className="hidden group-hover/pill:flex items-center gap-0.5 ml-1">
                          <button
                            data-testid={`rename-group-${g.id}`}
                            onClick={() => { setEditingGroupId(g.id); setEditingGroupLabel(g.label); }}
                            className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
                            title="Renommer"
                          >
                            <Edit2 className="w-2.5 h-2.5" />
                          </button>
                          <button
                            data-testid={`delete-group-${g.id}`}
                            onClick={() => {
                              const count = etapes.filter(e => e.groupe_pipeline === g.id).length;
                              if (!window.confirm(`Supprimer le groupe "${g.label}" ?${count > 0 ? ` ${count} étape(s) passeront en "Non groupé".` : ''}`)) return;
                              setGroupes(prev => prev.filter(gr => gr.id !== g.id));
                              setEtapes(prev => prev.map(e => e.groupe_pipeline === g.id ? { ...e, groupe_pipeline: '' } : e));
                            }}
                            className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-red-500/20 text-red-400 transition-colors"
                            title="Supprimer"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  {groupes.length === 0 && (
                    <p className="text-xs text-slate-500 italic">Aucun groupe créé</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    data-testid="new-group-input"
                    type="text"
                    value={newGroupLabel}
                    onChange={e => setNewGroupLabel(e.target.value)}
                    placeholder="Nouveau groupe..."
                    onKeyDown={e => {
                      if (e.key === 'Enter' && newGroupLabel.trim()) {
                        const id = newGroupLabel.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_àâäéèêëïîôùûüÿç]/g, '');
                        const colors = ['#3B82F6', '#8B5CF6', '#F59E0B', '#A855F7', '#06B6D4', '#EC4899', '#10B981', '#EF4444'];
                        setGroupes(prev => [...prev, { id, label: newGroupLabel.trim(), color: colors[prev.length % colors.length], ordre: prev.length + 1 }]);
                        setNewGroupLabel('');
                      }
                    }}
                    className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-violet-500"
                  />
                  <button
                    data-testid="add-group-btn"
                    onClick={() => {
                      if (!newGroupLabel.trim()) return;
                      const id = newGroupLabel.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_àâäéèêëïîôùûüÿç]/g, '');
                      const colors = ['#3B82F6', '#8B5CF6', '#F59E0B', '#A855F7', '#06B6D4', '#EC4899', '#10B981', '#EF4444'];
                      setGroupes(prev => [...prev, { id, label: newGroupLabel.trim(), color: colors[prev.length % colors.length], ordre: prev.length + 1 }]);
                      setNewGroupLabel('');
                    }}
                    className="px-3 py-1.5 bg-violet-600 text-white text-xs rounded-lg font-medium hover:bg-violet-500"
                  >
                    Ajouter
                  </button>
                </div>
              </div>

              {/* Étapes list with groupe_pipeline dropdown */}
              <div className="bg-slate-800/70 border border-slate-700/50 rounded-xl p-4 max-h-[500px] overflow-y-auto">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-slate-300">Étapes — Champ "Groupe"</h3>
                  <button
                    data-testid="save-etapes-groupes-btn"
                    onClick={saveEtapes}
                    disabled={saving}
                    className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs rounded-lg font-medium flex items-center gap-1.5"
                  >
                    <Save className="w-3.5 h-3.5" />
                    Sauvegarder étapes
                  </button>
                </div>
                <div className="space-y-1.5">
                  {etapes.map((e, idx) => {
                    const grp = groupes.find(g => g.id === e.groupe_pipeline);
                    return (
                      <div key={e.id} className="flex items-center gap-3 py-1.5 px-2 rounded-lg hover:bg-slate-700/30 group">
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: e.color }} />
                        <div className="flex-1 min-w-0">
                          <span className="text-xs text-white font-medium truncate block">{e.nom}</span>
                          <span className="text-[10px] text-slate-500">{availableCategories.find(c => c.id === e.categorie)?.label || e.categorie}</span>
                        </div>
                        <select
                          data-testid={`etape-groupe-${e.id}`}
                          value={e.groupe_pipeline || ''}
                          onChange={ev => updateEtape(idx, 'groupe_pipeline', ev.target.value)}
                          className="bg-slate-900 border border-slate-600 rounded px-2 py-1 text-[11px] text-white outline-none focus:border-violet-500 w-40"
                          style={grp ? { borderColor: `${grp.color}60` } : {}}
                        >
                          <option value="">— Aucun groupe —</option>
                          {groupes.map(g => (
                            <option key={g.id} value={g.id}>{g.label}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* RIGHT: Live preview */}
            <div className="space-y-4">
              <div className="bg-slate-800/70 border border-emerald-500/30 rounded-xl p-4">
                <h3 className="text-sm font-bold text-emerald-400 mb-3 flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Aperçu Dashboard — Approche A (pills avec expansion)
                </h3>

                {/* Dashboard selector */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {[
                    { id: 'all', label: 'Tous', color: '#64748b' },
                    ...dashboards.filter(d => d.actif && d.id !== 'aucun').map(d => ({ id: d.id, label: d.nom, color: '#3B82F6' }))
                  ].map(d => (
                    <button
                      key={d.id}
                      data-testid={`preview-dash-${d.id}`}
                      onClick={() => setPreviewDashboard(d.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                        previewDashboard === d.id ? 'scale-105 shadow-md bg-emerald-600 text-white' : 'hover:scale-105 bg-slate-700 text-slate-400'
                      }`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>

                {/* Preview with PipelineGroupedPills */}
                {(() => {
                  const filteredStages = previewDashboard === 'all'
                    ? etapes.filter(e => e.actif !== false)
                    : etapes.filter(e => e.actif !== false && getEtapeDashboards(e).includes(previewDashboard));
                  
                  // Build fake counts (0 for preview)
                  const fakeCounts = {};
                  filteredStages.forEach(s => { fakeCounts[s.id] = 0; });
                  
                  const filteredGroupes = groupes.filter(g =>
                    filteredStages.some(s => s.groupe_pipeline === g.id)
                  );
                  
                  if (filteredStages.length === 0) {
                    return <p className="text-xs text-slate-500 italic">Aucune étape assignée à ce dashboard</p>;
                  }
                  
                  return (
                    <PipelineGroupedPills
                      stages={filteredStages}
                      groups={filteredGroupes}
                      counts={fakeCounts}
                      horizontal={false}
                    />
                  );
                })()}
              </div>

              {/* Info box */}
              <div className="bg-violet-500/5 border border-violet-500/20 rounded-xl p-4">
                <h4 className="text-sm font-bold text-violet-400 mb-2">Comment ça marche</h4>
                <ul className="text-xs text-slate-400 space-y-1.5">
                  <li><strong className="text-white">1.</strong> Créez les groupes pipeline (colonne gauche, en haut)</li>
                  <li><strong className="text-white">2.</strong> Assignez chaque étape à un groupe via le dropdown</li>
                  <li><strong className="text-white">3.</strong> L'aperçu à droite montre le rendu pills en temps réel</li>
                  <li><strong className="text-white">4.</strong> Les étapes sans groupe apparaissent en pills individuelles</li>
                  <li><strong className="text-white">5.</strong> N'oubliez pas de <strong className="text-violet-300">sauvegarder les groupes ET les étapes</strong></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* TAB: ZONES */}
      {activeTab === 'zones' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-400">
              Sélectionnez les départements où vous intervenez. Les leads hors zone seront automatiquement filtrés.
            </p>
            <button
              onClick={saveZones}
              disabled={saving}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Sauvegarder
            </button>
          </div>
          
          {/* Zone active display */}
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
            <h4 className="text-emerald-400 font-medium mb-2 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Zones actives ({zones.length} départements)
            </h4>
            <div className="flex flex-wrap gap-2">
              {zones.length === 0 ? (
                <span className="text-slate-400 text-sm">Aucune zone sélectionnée</span>
              ) : (
                zones.map(dpt => (
                  <span key={dpt} className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-lg text-sm font-medium">
                    {dpt}
                  </span>
                ))
              )}
            </div>
          </div>
          
          {/* Département selector */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <h4 className="text-white font-medium mb-3">Tous les départements</h4>
            <div className="grid grid-cols-10 gap-2">
              {TOUS_DEPARTEMENTS.map(dpt => (
                <button
                  key={dpt}
                  onClick={() => toggleZone(dpt)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    zones.includes(dpt)
                      ? 'bg-emerald-500 text-white'
                      : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                  }`}
                >
                  {dpt}
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setZones(TOUS_DEPARTEMENTS.filter(d => !d.startsWith('97')))}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm"
              >
                France métropolitaine
              </button>
              <button
                onClick={() => setZones([])}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm"
              >
                Tout désélectionner
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* TAB: SOURCES */}
      {activeTab === 'sources' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-400">
              Configurez les sources de leads et leurs webhooks
            </p>
            <div className="flex gap-2">
              <button
                onClick={addSource}
                className="px-3 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg text-sm flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Ajouter source
              </button>
              <button
                onClick={saveSources}
                disabled={saving}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Sauvegarder
              </button>
            </div>
          </div>
          
          {/* Liste des sources */}
          <div className="space-y-3">
            {sources.map((source, index) => (
              <div
                key={source.id}
                className={`bg-slate-800/50 border rounded-xl p-4 ${
                  editingSource === index ? 'border-indigo-500' : 'border-slate-700'
                }`}
              >
                {editingSource === index ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs text-slate-400">ID (unique)</label>
                        <input
                          type="text"
                          value={source.id}
                          onChange={e => updateSource(index, 'id', e.target.value)}
                          className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400">Nom</label>
                        <input
                          type="text"
                          value={source.nom}
                          onChange={e => updateSource(index, 'nom', e.target.value)}
                          className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400">Type</label>
                        <select
                          value={source.type}
                          onChange={e => updateSource(index, 'type', e.target.value)}
                          className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm"
                        >
                          <option value="formulaire">Formulaire Web</option>
                          <option value="zapier">Zapier</option>
                          <option value="call">Call Center</option>
                          <option value="ringover">Ringover</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-slate-400">URL Webhook</label>
                        <input
                          type="text"
                          value={source.webhook_url || ''}
                          onChange={e => updateSource(index, 'webhook_url', e.target.value)}
                          className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm font-mono"
                          placeholder="/api/webhook/..."
                        />
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={source.actif}
                            onChange={e => updateSource(index, 'actif', e.target.checked)}
                            className="w-4 h-4 rounded"
                          />
                          <span className="text-sm">Source active</span>
                        </label>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-3 h-3 rounded-full ${source.actif ? 'bg-emerald-400' : 'bg-slate-500'}`} />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{source.nom}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            source.type === 'formulaire' ? 'bg-blue-500/20 text-blue-400' :
                            source.type === 'zapier' ? 'bg-orange-500/20 text-orange-400' :
                            source.type === 'call' ? 'bg-purple-500/20 text-purple-400' :
                            'bg-green-500/20 text-green-400'
                          }`}>
                            {source.type}
                          </span>
                        </div>
                        {source.webhook_url && (
                          <p className="text-sm text-slate-400 font-mono">{source.webhook_url}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingSource(editingSource === index ? null : index)}
                        className="p-2 hover:bg-slate-700 rounded-lg"
                      >
                        {editingSource === index ? <Check className="w-4 h-4 text-green-400" /> : <Edit2 className="w-4 h-4 text-slate-400" />}
                      </button>
                      <button
                        onClick={() => deleteSource(index)}
                        className="p-2 hover:bg-red-500/20 rounded-lg text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Webhook Info */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <h4 className="text-blue-400 font-medium mb-2 flex items-center gap-2">
              <Link2 className="w-4 h-4" />
              Utilisation des Webhooks
            </h4>
            <p className="text-sm text-slate-400 mb-4">
              Envoyez vos leads en POST JSON à l'URL webhook. Format attendu:
            </p>
            <pre className="p-3 bg-slate-900 rounded-lg text-xs text-slate-300 overflow-x-auto">
{`{
  "nom": "Dupont",
  "prenom": "Jean",
  "telephone": "0612345678",
  "email": "jean@example.com",
  "adresse": "123 Rue Example",
  "code_postal": "75001",
  "ville": "Paris",
  "source": "prime-adapt-seniors"
}`}
            </pre>
          </div>
          
          {/* URLs Webhooks à copier */}
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
            <h4 className="text-emerald-400 font-medium mb-3 flex items-center gap-2">
              <Link2 className="w-4 h-4" />
              URLs Webhooks (à copier)
            </h4>
            <div className="space-y-3">
              {sources.filter(s => s.webhook_url).map(source => {
                const fullUrl = `${getApiUrl()}${source.webhook_url}`;
                return (
                  <div key={source.id} className="flex items-center justify-between bg-slate-900 rounded-lg p-3">
                    <div>
                      <p className="text-sm text-white font-medium">{source.nom}</p>
                      <code className="text-xs text-emerald-400 font-mono">{fullUrl}</code>
                    </div>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(fullUrl);
                        alert('✅ URL copiée !');
                      }}
                      className="px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg text-sm"
                    >
                      Copier
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      
      {/* TAB: DASHBOARDS */}
      {activeTab === 'dashboards' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-400">
              Sélectionnez les dashboards disponibles dans le sélecteur de chaque étape du pipeline
            </p>
            <button
              data-testid="save-dashboards-btn"
              onClick={saveDashboards}
              disabled={saving}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Sauvegarder
            </button>
          </div>
          
          {/* Liste des dashboards */}
          <div className="space-y-2">
            {dashboards.filter(d => d.id !== 'aucun').map((dashboard, index) => {
              const associatedCount = etapes.filter(e => getEtapeDashboards(e).includes(dashboard.id)).length;
              return (
                <div
                  key={dashboard.id}
                  data-testid={`dashboard-item-${dashboard.id}`}
                  className={`flex items-center justify-between bg-slate-800/50 border rounded-xl p-4 transition-all ${
                    dashboard.actif ? 'border-indigo-500/30' : 'border-slate-700 opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      onClick={() => toggleDashboardActive(index)}
                      className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${
                        dashboard.actif ? 'bg-indigo-500' : 'bg-slate-600'
                      }`}
                    >
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        dashboard.actif ? 'translate-x-7' : 'translate-x-1'
                      }`} />
                    </div>
                    <div>
                      <span className="font-medium text-white">{dashboard.nom}</span>
                      <span className="text-xs text-slate-500 font-mono ml-2">({dashboard.id})</span>
                      {dashboard.description && (
                        <p className="text-xs text-slate-400 mt-0.5">{dashboard.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`text-sm font-medium ${associatedCount > 0 ? 'text-indigo-400' : 'text-slate-500'}`}>
                      {associatedCount} étape{associatedCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Info */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <h4 className="text-blue-400 font-medium mb-2 flex items-center gap-2">
              <LayoutDashboard className="w-4 h-4" />
              Comment ça marche
            </h4>
            <p className="text-sm text-slate-400">
              Activez les dashboards qui doivent apparaître dans le multi-sélecteur de chaque étape du pipeline.
              Une étape peut être associée à plusieurs dashboards (ex: "Lead" visible dans Télépro ET Administratif).
              La liste provient automatiquement des dashboards configurés dans l'application.
            </p>
          </div>
        </div>
      )}

      {/* TAB: CATÉGORIES */}
      {activeTab === 'categories' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-400">
              Gérez les catégories du workflow (utilisées dans le DAG et le filtre des étapes)
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setCategories([...categories, {
                    id: `categorie_${Date.now()}`,
                    label: 'Nouvelle catégorie',
                    color: 'slate',
                    ordre: categories.length + 1
                  }]);
                }}
                className="px-3 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg text-sm flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Ajouter
              </button>
              <button
                onClick={async () => {
                  setSaving(true);
                  try {
                    const resp = await authFetch(`${API}/config/pipeline/categories`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(categories)
                    });
                    if (resp.ok) {
                      alert('Catégories sauvegardées');
                    } else {
                      const err = await resp.json().catch(() => ({}));
                      alert('Erreur: ' + (err.detail || resp.statusText));
                    }
                  } catch (e) {
                    alert('Erreur: ' + e.message);
                  }
                  setSaving(false);
                }}
                disabled={saving}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Sauvegarder
              </button>
            </div>
          </div>

          <div className="space-y-2">
            {categories.map((cat, index) => {
              const usageCount = etapes.filter(e => e.categorie === cat.id).length;
              return (
                <div
                  key={`cat-${index}`}
                  className="bg-slate-800/50 border border-slate-700 rounded-xl p-4"
                >
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-slate-500 font-mono w-6">{cat.ordre}</span>
                    <div className="flex-1 grid grid-cols-4 gap-3">
                      <div>
                        <label className="text-xs text-slate-400">ID</label>
                        <input
                          type="text"
                          value={cat.id}
                          onChange={e => {
                            const nc = [...categories];
                            nc[index] = { ...nc[index], id: e.target.value };
                            setCategories(nc);
                          }}
                          className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm font-mono"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400">Label</label>
                        <input
                          type="text"
                          value={cat.label}
                          onChange={e => {
                            const nc = [...categories];
                            nc[index] = { ...nc[index], label: e.target.value };
                            setCategories(nc);
                          }}
                          className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400">Couleur</label>
                        <select
                          value={cat.color}
                          onChange={e => {
                            const nc = [...categories];
                            nc[index] = { ...nc[index], color: e.target.value };
                            setCategories(nc);
                          }}
                          className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm"
                        >
                          {['blue', 'purple', 'emerald', 'amber', 'red', 'orange', 'cyan', 'pink', 'slate'].map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-slate-400">Ordre</label>
                        <input
                          type="number"
                          value={cat.ordre}
                          onChange={e => {
                            const nc = [...categories];
                            nc[index] = { ...nc[index], ordre: parseFloat(e.target.value) || 0 };
                            setCategories(nc);
                          }}
                          className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-1 rounded ${usageCount > 0 ? 'bg-indigo-500/20 text-indigo-400' : 'bg-slate-700 text-slate-500'}`}>
                        {usageCount} étape{usageCount !== 1 ? 's' : ''}
                      </span>
                      <button
                        onClick={() => {
                          if (usageCount > 0) {
                            alert(`Impossible de supprimer : ${usageCount} étape(s) utilisent cette catégorie.`);
                            return;
                          }
                          if (confirm(`Supprimer la catégorie "${cat.label}" ?`)) {
                            setCategories(categories.filter((_, i) => i !== index));
                          }
                        }}
                        className="p-2 hover:bg-red-500/20 rounded-lg text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <h4 className="text-blue-400 font-medium mb-2 flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Comment ça marche
            </h4>
            <p className="text-sm text-slate-400">
              Les catégories sont utilisées pour organiser les étapes du pipeline dans le DAG (filtres dynamiques) et dans le dropdown de chaque étape. Le compteur indique combien d'étapes sont assignées à chaque catégorie. Une catégorie ne peut pas être supprimée si des étapes l'utilisent.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
